// ###### IMPORTS
import db from './db';

// ###### ID MAPPER CLASS
class IdMapper {
  // ##### MAP TEMP ID TO REAL ID
  async mapTempToReal(tempId, realId, storeName) {
    if (!tempId || !tempId.toString().startsWith('temp_')) {
      return; // Not a temporary ID
    }

    try {
      await db.id_mapping.add({
        temp_id: tempId.toString(),
        real_id: realId,
        store_name: storeName,
        created_at: new Date().toISOString()
      });
    } catch (error) {
      // If mapping already exists, update it
      if (error.name === 'ConstraintError') {
        await db.id_mapping.update({ temp_id: tempId.toString() }, {
          real_id: realId,
          store_name: storeName
        });
      } else {
        console.error('Error mapping temp ID to real ID:', error);
      }
    }
  }

  // ##### GET REAL ID FROM TEMP ID
  async getRealId(tempId) {
    if (!tempId || !tempId.toString().startsWith('temp_')) {
      return tempId; // Already a real ID
    }

    try {
      const mapping = await db.id_mapping.get({ temp_id: tempId.toString() });
      return mapping ? mapping.real_id : null;
    } catch (error) {
      console.error('Error getting real ID from temp ID:', error);
      return null;
    }
  }

  // ##### RESOLVE RELATIONSHIPS IN DATA
  async resolveRelationships(data, storeName, relationshipFields) {
    if (!data || !relationshipFields || relationshipFields.length === 0) {
      return data;
    }

    const resolvedData = { ...data };

    for (const field of relationshipFields) {
      if (resolvedData[field] && resolvedData[field].toString().startsWith('temp_')) {
        const realId = await this.getRealId(resolvedData[field]);
        if (realId) {
          resolvedData[field] = realId;
        } else {
          // If temp ID not yet resolved, keep it (will be resolved during sync)
          console.warn(`Temp ID ${resolvedData[field]} for field ${field} not yet resolved`);
        }
      }
    }

    return resolvedData;
  }

  // ##### RESOLVE ALL TEMP IDS IN DATA OBJECT
  async resolveAllTempIds(data, storeName) {
    if (!data) return data;

    const resolvedData = { ...data };

    // Resolve main ID if temporary
    if (resolvedData.id && resolvedData.id.toString().startsWith('temp_')) {
      const realId = await this.getRealId(resolvedData.id);
      if (realId) {
        resolvedData.id = realId;
      }
    }

    // Resolve common relationship fields
    const commonFields = ['cluster_id', 'todo_id', 'trip_id', 'account_id', 'parent_id', 'project', 'column'];
    for (const field of commonFields) {
      if (resolvedData[field] && resolvedData[field].toString().startsWith('temp_')) {
        const realId = await this.getRealId(resolvedData[field]);
        if (realId) {
          resolvedData[field] = realId;
        }
      }
    }

    return resolvedData;
  }

  // ##### CLEANUP OLD MAPPINGS
  async cleanupOldMappings(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);
      
      await db.id_mapping
        .where('created_at')
        .below(cutoffDate.toISOString())
        .delete();
    } catch (error) {
      console.error('Error cleaning up old mappings:', error);
    }
  }

  // ##### GET ALL MAPPINGS FOR STORE
  async getMappingsForStore(storeName) {
    try {
      return await db.id_mapping
        .where('store_name')
        .equals(storeName)
        .toArray();
    } catch (error) {
      console.error('Error getting mappings for store:', error);
      return [];
    }
  }
}

export const idMapper = new IdMapper();
export default idMapper;
